import { Component, DestroyRef, OnInit, inject, signal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormsModule } from '@angular/forms';
import { ActivatedRoute, Router, RouterLink } from '@angular/router';
import { ButtonModule } from 'primeng/button';
import { CheckboxModule } from 'primeng/checkbox';
import { DialogModule } from 'primeng/dialog';
import { InputTextModule } from 'primeng/inputtext';
import { MultiSelectModule } from 'primeng/multiselect';
import { SelectModule } from 'primeng/select';
import { TableModule } from 'primeng/table';
import { filter, finalize } from 'rxjs';
import { CurrentContextService } from '../../../core/context/current-context.service';
import {
  LiveUpdateEventTypes,
  growthSearchOpsTopic,
  liveStatusTranslationKey
} from '../../../core/live-updates/live-update.models';
import { LiveUpdateService } from '../../../core/live-updates/live-update.service';
import { LocalizationService } from '../../../core/localization/localization.service';
import { AinFormFieldComponent } from '../../../shared/design-system/form-field.component';
import { InfoHintComponent } from '../../../shared/design-system/info-hint.component';
import { AinPageComponent } from '../../../shared/design-system/page.component';
import { AinStateComponent } from '../../../shared/design-system/state.component';
import { StructuredJsonFormComponent } from '../../../shared/design-system/structured-json-form.component';
import {
  SEARCH_OPS_CMS_TYPES,
  SEARCH_OPS_LOCALES,
  SEARCH_OPS_SITE_KINDS,
  SEARCH_OPS_STATUSES,
  SearchPropertyListItemDto,
  searchOpsOnboardingFields
} from './growth-search-ops.models';
import { GrowthSearchOpsService } from './growth-search-ops.service';
import { GrowthSearchOpsStorefrontBindingComponent } from './growth-search-ops-storefront-binding.component';

@Component({
  standalone: true,
  selector: 'ain-growth-search-ops-properties',
  imports: [
    AinPageComponent,
    AinStateComponent,
    InfoHintComponent,
    AinFormFieldComponent,
    StructuredJsonFormComponent,
    GrowthSearchOpsStorefrontBindingComponent,
    FormsModule,
    TableModule,
    ButtonModule,
    InputTextModule,
    SelectModule,
    MultiSelectModule,
    CheckboxModule,
    DialogModule,
    RouterLink
  ],
  template: `
    <ain-page
      [eyebrow]="t('growth.eyebrow')"
      [title]="t('growth.searchops.properties.title')"
      [description]="t('growth.searchops.properties.description')">
      <ain-info-hint
        page-title-addon
        [titleKey]="'growth.searchops.properties.help.title'"
        [descriptionKey]="'growth.searchops.properties.help.description'"
        [bulletKeys]="[
          'growth.searchops.properties.help.bullets.domain',
          'growth.searchops.properties.help.bullets.siteKind',
          'growth.searchops.properties.help.bullets.team'
        ]" />

      <section class="card list-card">
        <div class="toolbar">
          <button pButton type="button" [label]="t('growth.searchops.properties.actions.create')" (click)="openCreate()"></button>
          <button pButton type="button" severity="secondary" [label]="t('common.refresh')" (click)="load(true)"></button>
          <a pButton severity="secondary" [routerLink]="['/growth/search-ops']" [label]="t('growth.searchops.properties.actions.workHome')"></a>
          <span class="live-badge">{{ t(liveStatusKey(liveUpdateService.connectionState())) }}</span>
        </div>
        <div class="toolbar">
          <button pButton type="button" severity="secondary" [label]="t('growth.searchops.properties.filters.toggle')" (click)="filtersOpen.set(!filtersOpen())"></button>
        </div>
        @if (filtersOpen()) {
          <div class="filters">
            <input pInputText [(ngModel)]="searchTerm" [placeholder]="t('growth.searchops.properties.search')" />
            <p-select
              [options]="siteKindOptions()"
              optionLabel="label"
              optionValue="value"
              [(ngModel)]="siteKindFilter"
              [showClear]="true"
              [placeholder]="t('growth.searchops.fields.siteKind')"
              appendTo="body" />
            <button pButton type="button" [label]="t('growth.searchops.properties.actions.filter')" (click)="applyFilters()"></button>
          </div>
        }

        @if (loading()) {
          <ain-state type="loading" [title]="t('common.loading')" />
        } @else if (error()) {
          <ain-state type="error" [title]="t('common.error')" [message]="error()" />
        } @else if (filtered().length === 0) {
          <ain-state type="empty" [title]="t('growth.searchops.properties.empty')" />
        } @else {
          <p-table
            [value]="filtered()"
            [(selection)]="selected"
            dataKey="id"
            [paginator]="true"
            [rows]="pageSize"
            [first]="first"
            (onPage)="onPage($event)"
            [lazy]="false">
            <ng-template pTemplate="header">
              <tr>
                <th style="width: 3rem"><p-tableHeaderCheckbox /></th>
                <th>{{ t('growth.searchops.fields.name') }}</th>
                <th>{{ t('growth.searchops.fields.primaryDomain') }}</th>
                <th>{{ t('growth.searchops.fields.siteKind') }}</th>
                <th>{{ t('growth.searchops.fields.status') }}</th>
                <th>{{ t('growth.searchops.fields.seats') }}</th>
              </tr>
            </ng-template>
            <ng-template pTemplate="body" let-row>
              <tr>
                <td><p-tableCheckbox [value]="row" /></td>
                <td><a [routerLink]="['/growth/search-ops/properties', row.id]" [queryParams]="listStateParams()">{{ row.name }}</a></td>
                <td>{{ row.primaryDomain }}</td>
                <td>{{ t('growth.searchops.siteKind.' + row.siteKind) }}</td>
                <td>{{ t('growth.searchops.status.' + row.status) }}</td>
                <td>{{ row.seatCount }}</td>
              </tr>
            </ng-template>
          </p-table>
        }
      </section>
    </ain-page>

    <p-dialog
      [header]="t('growth.searchops.properties.createTitle')"
      [(visible)]="createVisible"
      [modal]="true"
      [style]="{ width: '42rem' }"
      appendTo="body">
      <ain-info-hint
        [titleKey]="'growth.searchops.onboard.help.title'"
        [descriptionKey]="'growth.searchops.onboard.help.description'" />
      <div class="dialog-grid">
        <ain-form-field [label]="t('growth.searchops.fields.name')">
          <input pInputText [(ngModel)]="createModel.name" />
        </ain-form-field>
        <ain-form-field [label]="t('growth.searchops.fields.primaryDomain')">
          <input pInputText [(ngModel)]="createModel.primaryDomain" />
        </ain-form-field>
        <ain-form-field [label]="t('growth.searchops.fields.siteKind')">
          <p-select [options]="siteKindOptions()" optionLabel="label" optionValue="value" [(ngModel)]="createModel.siteKind" appendTo="body" (ngModelChange)="onSiteKindChange($event)" />
        </ain-form-field>
        @if (createModel.siteKind !== 'ain_storefront') {
          <ain-form-field [label]="t('growth.searchops.fields.cmsType')">
            <p-select [options]="cmsOptions()" optionLabel="label" optionValue="value" [(ngModel)]="createModel.cmsType" appendTo="body" />
          </ain-form-field>
        }
        <ain-form-field [label]="t('growth.searchops.fields.status')">
          <p-select [options]="statusOptions()" optionLabel="label" optionValue="value" [(ngModel)]="createModel.status" appendTo="body" />
        </ain-form-field>
        <ain-form-field [label]="t('growth.searchops.fields.locales')">
          <p-multiSelect [options]="localeOptions()" optionLabel="label" optionValue="value" [(ngModel)]="createLocales" appendTo="body" />
        </ain-form-field>
        @if (createModel.siteKind === 'ain_storefront') {
          <div class="full">
            <ain-growth-search-ops-storefront-binding [(payloadJson)]="createModel.storefrontBindingJson" />
          </div>
        }
        <div class="full">
          <ain-form-field [label]="t('growth.searchops.fields.onboarding')">
            <ain-structured-json-form [fields]="onboardingFields" [(payloadJson)]="createModel.onboardingJson" />
          </ain-form-field>
        </div>
      </div>
      <ng-template pTemplate="footer">
        <button pButton type="button" severity="secondary" [label]="t('common.cancel')" (click)="createVisible = false"></button>
        <button pButton type="button" [label]="t('common.save')" [loading]="saving()" (click)="saveCreate()"></button>
      </ng-template>
    </p-dialog>
  `,
  styles: [`
    .toolbar { display: flex; gap: 8px; align-items: center; flex-wrap: wrap; margin-bottom: 10px; }
    .filters { display: flex; gap: 8px; flex-wrap: wrap; margin-bottom: 12px; }
    .card { background: var(--ain-color-surface, #fff); padding: 16px; border-radius: 12px; }
    .live-badge { font-size: 0.85rem; color: #64748b; }
    .dialog-grid { display: grid; grid-template-columns: repeat(2, minmax(0, 1fr)); gap: 10px; margin-top: 10px; }
    .dialog-grid .full { grid-column: 1 / -1; }
  `]
})
export class GrowthSearchOpsPropertiesComponent implements OnInit {
  private readonly api = inject(GrowthSearchOpsService);
  private readonly localization = inject(LocalizationService);
  private readonly route = inject(ActivatedRoute);
  private readonly router = inject(Router);
  private readonly context = inject(CurrentContextService);
  private readonly destroyRef = inject(DestroyRef);
  readonly liveUpdateService = inject(LiveUpdateService);
  readonly liveStatusKey = liveStatusTranslationKey;
  readonly onboardingFields = searchOpsOnboardingFields;

  readonly rows = signal<SearchPropertyListItemDto[]>([]);
  readonly filtered = signal<SearchPropertyListItemDto[]>([]);
  readonly loading = signal(false);
  readonly saving = signal(false);
  readonly error = signal('');
  readonly filtersOpen = signal(true);
  selected: SearchPropertyListItemDto[] = [];
  searchTerm = '';
  siteKindFilter: string | null = null;
  pageSize = 10;
  first = 0;
  createVisible = false;
  createLocales: string[] = ['fa-IR'];
  createModel = this.emptyCreate();
  private subscribedTopic: string | null = null;

  ngOnInit(): void {
    this.liveUpdateService.events$
      .pipe(filter(e => e.eventType === LiveUpdateEventTypes.growthSearchOpsChanged), takeUntilDestroyed(this.destroyRef))
      .subscribe(() => this.load(false));
    this.destroyRef.onDestroy(() => {
      if (this.subscribedTopic) {
        void this.liveUpdateService.unsubscribe(this.subscribedTopic);
        this.subscribedTopic = null;
      }
    });
    this.route.queryParamMap.pipe(takeUntilDestroyed(this.destroyRef)).subscribe(params => {
      this.searchTerm = params.get('q') ?? '';
      this.siteKindFilter = params.get('siteKind');
      this.first = Number(params.get('first') ?? 0);
      this.load(true);
    });
    void this.setupLiveSubscription();
  }

  t(key: string): string {
    return this.localization.translate(key);
  }

  siteKindOptions(): Array<{ label: string; value: string }> {
    return SEARCH_OPS_SITE_KINDS.map(value => ({ value, label: this.t(`growth.searchops.siteKind.${value}`) }));
  }

  cmsOptions(): Array<{ label: string; value: string }> {
    return SEARCH_OPS_CMS_TYPES.map(value => ({ value, label: this.t(`growth.searchops.cmsType.${value}`) }));
  }

  statusOptions(): Array<{ label: string; value: string }> {
    return SEARCH_OPS_STATUSES.map(value => ({ value, label: this.t(`growth.searchops.status.${value}`) }));
  }

  localeOptions(): Array<{ label: string; value: string }> {
    return SEARCH_OPS_LOCALES.map(value => ({ value, label: value }));
  }

  listStateParams(): Record<string, string | number> {
    const params: Record<string, string | number> = {};
    if (this.searchTerm) params['q'] = this.searchTerm;
    if (this.siteKindFilter) params['siteKind'] = this.siteKindFilter;
    if (this.first) params['first'] = this.first;
    return params;
  }

  applyFilters(): void {
    void this.router.navigate([], {
      relativeTo: this.route,
      queryParams: this.listStateParams()
    });
  }

  onPage(event: { first?: number; rows?: number }): void {
    this.first = event.first ?? 0;
    this.pageSize = event.rows ?? this.pageSize;
    this.applyFilters();
  }

  openCreate(): void {
    this.createModel = this.emptyCreate();
    this.createLocales = ['fa-IR'];
    this.createVisible = true;
  }

  onSiteKindChange(siteKind: string): void {
    if (siteKind === 'ain_storefront') {
      this.createModel.cmsType = 'ain';
      return;
    }
    this.createModel.storefrontBindingJson = '{}';
  }

  saveCreate(): void {
    this.saving.set(true);
    this.api.createProperty({
      ...this.createModel,
      localesJson: JSON.stringify(this.createLocales ?? []),
      cmsType: this.createModel.siteKind === 'ain_storefront' ? 'ain' : this.createModel.cmsType,
      storefrontBindingJson: this.createModel.siteKind === 'ain_storefront' ? this.createModel.storefrontBindingJson : '{}'
    }).pipe(
      takeUntilDestroyed(this.destroyRef),
      finalize(() => this.saving.set(false))
    ).subscribe({
      next: created => {
        this.createVisible = false;
        void this.router.navigate(['/growth/search-ops/properties', created.id]);
      },
      error: err => this.error.set(err?.error?.error ?? this.t('growth.searchops.messages.saveFailed'))
    });
  }

  load(showSpinner: boolean): void {
    if (showSpinner) this.loading.set(true);
    this.error.set('');
    this.api.listProperties().pipe(
      takeUntilDestroyed(this.destroyRef),
      finalize(() => this.loading.set(false))
    ).subscribe({
      next: rows => {
        this.rows.set(rows);
        const term = this.searchTerm.trim().toLowerCase();
        this.filtered.set(rows.filter(row =>
          (!term || row.name.toLowerCase().includes(term) || row.primaryDomain.toLowerCase().includes(term))
          && (!this.siteKindFilter || row.siteKind === this.siteKindFilter)));
      },
      error: err => this.error.set(err?.error?.error ?? this.t('growth.searchops.messages.loadFailed'))
    });
  }

  private emptyCreate() {
    return {
      name: '',
      primaryDomain: '',
      siteKind: 'external',
      cmsType: 'wordpress',
      status: 'draft',
      storefrontBindingJson: '{}',
      onboardingJson: '{}'
    };
  }

  private async setupLiveSubscription(): Promise<void> {
    const tenantId = this.context.context()?.tenantId;
    if (!tenantId) return;
    this.subscribedTopic = growthSearchOpsTopic(tenantId);
    await this.liveUpdateService.subscribe(this.subscribedTopic);
  }
}
